import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi';
import { z } from 'zod';

// src/common/openapi.ts
extendZodWithOpenApi(z);
z.config({
  customError: (issue) => {
    switch (issue.code) {
      case "invalid_type":
        return "This field is required";
      case "too_small":
        if (issue.origin === "string") {
          return issue.minimum === 1 ? "This field is required" : `Enter at least ${issue.minimum} characters`;
        }
        if (issue.origin === "number") return `Enter a value of at least ${issue.minimum}`;
        if (issue.origin === "array") return "Select at least one option";
        return void 0;
      case "too_big":
        if (issue.origin === "string") return `Enter at most ${issue.maximum} characters`;
        if (issue.origin === "number") return `Enter a value of at most ${issue.maximum}`;
        return void 0;
      case "invalid_format":
        if (issue.format === "uuid") return "Select an option";
        if (issue.format === "email") return "Enter a valid email address";
        if (issue.format === "url") return "Enter a valid URL";
        return "Enter a valid value";
      default:
        return void 0;
    }
  }
});

// src/common/index.ts
var uuidSchema = z.uuid("Select an option").openapi("Uuid");
z.coerce.number().int().positive().openapi("Id");
var slugSchema = z.string().trim().min(1, "Enter a slug").max(64).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Use lowercase letters, numbers and hyphens only").openapi("Slug");
var timestampSchema = z.iso.datetime({ offset: true }).openapi("Timestamp");
z.iso.date().openapi("DateOnly");
z.email("Enter a valid email address").max(254).openapi("Email");
var phoneSchema = z.string().trim().regex(/^\+?[0-9]{7,15}$/, "Enter a valid phone number").openapi("Phone");
var pincodeSchema = z.string().trim().regex(/^[A-Za-z0-9]{4,10}$/, "Enter a valid pincode").openapi("Pincode");
var countryCodeSchema = z.string().trim().regex(/^[A-Z]{2}$/, "Enter a valid country code").openapi("CountryCode");
var currencyCodeSchema = z.string().trim().regex(/^[A-Z]{3}$/).openapi("CurrencyCode");
var urlSchema = z.url().max(2048).openapi("Url");
var moneySchema = z.string().regex(/^\d{1,10}\.\d{2}$/).openapi("Money");
z.string().regex(/^-?\d{1,10}\.\d{2}$/).openapi("SignedMoney");
var percentSchema = z.number().min(0).max(100).openapi("Percent");
z.number().int().min(0).openapi("Quantity");
var idempotencyKeySchema = z.string().trim().min(8).max(128).openapi("IdempotencyKey");
var requestIdSchema = z.string().trim().min(1).max(64).openapi("RequestId");
var ErrorCode = {
  VALIDATION_ERROR: "VALIDATION_ERROR",
  UNAUTHORIZED: "UNAUTHORIZED",
  FORBIDDEN: "FORBIDDEN",
  NOT_FOUND: "NOT_FOUND",
  CONFLICT: "CONFLICT",
  RATE_LIMITED: "RATE_LIMITED",
  PAYMENT_FAILED: "PAYMENT_FAILED",
  INTERNAL_ERROR: "INTERNAL_ERROR"
};
var errorCodeSchema = z.enum(ErrorCode).openapi("ErrorCode");
var errorDetailSchema = z.object({ path: z.string(), message: z.string() }).openapi("ErrorDetail");
var errorBodySchema = z.object({
  code: errorCodeSchema,
  message: z.string(),
  details: z.array(errorDetailSchema).optional(),
  requestId: requestIdSchema
}).openapi("ErrorBody");
z.object({ error: errorBodySchema }).openapi("ErrorResponse");
var MAX_PAGE_SIZE = 100;
z.object({
  page: z.number().int().min(1),
  limit: z.number().int().min(1).max(MAX_PAGE_SIZE),
  total: z.number().int().min(0),
  totalPages: z.number().int().min(0)
}).openapi("PaginationMeta");
var paginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(20)
}).openapi("PaginationQuery");
var sortOrderSchema = z.enum(["asc", "desc"]).openapi("SortOrder");
var sortQuerySchema = z.object({
  sort: z.string().trim().min(1).max(64).optional(),
  order: sortOrderSchema.default("desc")
}).openapi("SortQuery");
var searchQuerySchema = z.object({ search: z.string().trim().min(1).max(160).optional() }).openapi("SearchQuery");
var listQuerySchema = paginationQuerySchema.extend(sortQuerySchema.shape).extend(searchQuerySchema.shape).openapi("ListQuery");
z.object({ dateFrom: timestampSchema.optional(), dateTo: timestampSchema.optional() }).openapi("DateRangeQuery");
var booleanQuerySchema = z.union([z.boolean(), z.stringbool()]).openapi("BooleanQuery");
z.object({ id: uuidSchema }).openapi("IdParam");
z.object({ slug: slugSchema }).openapi("SlugParam");
z.object({ ids: z.array(uuidSchema).min(1).max(100) }).openapi("BulkIds");
var actorSchema = z.object({ id: uuidSchema, name: z.string(), role: z.string() }).openapi("Actor");
z.object({ data: z.object({ success: z.literal(true) }) }).openapi("SuccessResponse");
z.object({ data: z.object({ count: z.number().int().min(0) }) }).openapi("CountResponse");

// src/media/index.ts
var MediaKind = {
  IMAGE: "image",
  VIDEO: "video",
  DOCUMENT: "document"
};
var mediaKindSchema = z.enum(MediaKind).openapi("MediaKind");
var MediaFolder = {
  PRODUCTS: "products",
  BRANDS: "brands",
  CATEGORIES: "categories",
  VEHICLES: "vehicles",
  SETTINGS: "settings",
  RETURNS: "returns",
  SEARCH: "search"
};
var mediaFolderSchema = z.enum(MediaFolder).openapi("MediaFolder");
var mediaKeySchema = z.string().trim().min(1).max(512).regex(/^[A-Za-z0-9!_.*'()\-/]+$/).openapi("MediaKey");
var presignRequestSchema = z.object({
  fileName: z.string().trim().min(1).max(255),
  contentType: z.string().trim().regex(/^[\w.+-]+\/[\w.+-]+$/),
  kind: mediaKindSchema,
  folder: mediaFolderSchema,
  sizeBytes: z.number().int().positive().max(50 * 1024 * 1024)
}).openapi("PresignRequest");
var presignResponseSchema = z.object({
  key: mediaKeySchema,
  uploadUrl: urlSchema,
  publicUrl: urlSchema,
  method: z.enum(["PUT", "POST"]),
  headers: z.record(z.string(), z.string()),
  fields: z.record(z.string(), z.string()).optional(),
  expiresAt: timestampSchema
}).openapi("PresignResponse");
z.object({
  fileName: z.string().trim().min(1).max(255),
  contentType: z.string().trim().regex(/^image\/[\w.+-]+$/),
  sizeBytes: z.number().int().positive().max(10 * 1024 * 1024)
}).openapi("SearchImagePresignRequest");
z.object({ files: z.array(presignRequestSchema).min(1).max(10) }).openapi("PresignBatchRequest");
z.object({ uploads: z.array(presignResponseSchema) }).openapi("PresignBatchResponse");
z.object({
  id: uuidSchema,
  key: mediaKeySchema,
  url: urlSchema,
  kind: mediaKindSchema,
  folder: mediaFolderSchema,
  contentType: z.string(),
  sizeBytes: z.number().int().min(0),
  width: z.number().int().positive().nullable(),
  height: z.number().int().positive().nullable(),
  createdAt: timestampSchema
}).openapi("MediaAsset");
z.object({
  key: mediaKeySchema,
  kind: mediaKindSchema,
  folder: mediaFolderSchema,
  contentType: z.string().trim().min(1).max(160),
  sizeBytes: z.number().int().min(0),
  width: z.number().int().positive().nullable().optional(),
  height: z.number().int().positive().nullable().optional()
}).openapi("MediaAssetCreate");
z.object({
  kind: mediaKindSchema,
  folder: mediaFolderSchema,
  width: z.number().int().positive().nullable(),
  height: z.number().int().positive().nullable()
}).partial().openapi("MediaAssetUpdate");
listQuerySchema.extend({ kind: mediaKindSchema.optional(), folder: mediaFolderSchema.optional() }).openapi("MediaQuery");
z.object({ keys: z.array(mediaKeySchema).min(1).max(100) }).openapi("MediaDeleteRequest");

// src/vehicles/index.ts
var vehicleAttributesSchema = z.record(z.string(), z.unknown()).openapi("VehicleAttributes");
z.object({
  id: uuidSchema,
  slug: slugSchema,
  name: z.string(),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema
}).openapi("VehicleType");
z.object({
  slug: slugSchema,
  name: z.string().trim().min(1).max(80),
  sortOrder: z.number().int().min(0).default(0),
  isActive: z.boolean().default(true)
}).openapi("VehicleTypeCreate");
z.object({
  slug: slugSchema,
  name: z.string().trim().min(1).max(80),
  sortOrder: z.number().int().min(0),
  isActive: z.boolean()
}).partial().openapi("VehicleTypeUpdate");
listQuerySchema.extend({ isActive: booleanQuerySchema.optional() }).openapi("VehicleTypeQuery");
z.object({
  id: uuidSchema,
  vehicleTypeId: uuidSchema,
  vehicleTypeSlug: slugSchema,
  name: z.string(),
  slug: slugSchema,
  logoUrl: urlSchema.nullable(),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
  modelCount: z.number().int().min(0).optional(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema
}).openapi("VehicleMake");
z.object({
  vehicleTypeId: uuidSchema,
  name: z.string().trim().min(1).max(120),
  slug: slugSchema,
  logoUrl: urlSchema.nullable().optional(),
  sortOrder: z.number().int().min(0).default(0),
  isActive: z.boolean().default(true)
}).openapi("VehicleMakeCreate");
z.object({
  vehicleTypeId: uuidSchema,
  name: z.string().trim().min(1).max(120),
  slug: slugSchema,
  logoUrl: urlSchema.nullable(),
  sortOrder: z.number().int().min(0),
  isActive: z.boolean()
}).partial().openapi("VehicleMakeUpdate");
listQuerySchema.extend({
  vehicleTypeId: uuidSchema.optional(),
  vehicleTypeSlug: slugSchema.optional(),
  isActive: booleanQuerySchema.optional()
}).openapi("VehicleMakeQuery");
z.object({
  id: uuidSchema,
  makeId: uuidSchema,
  makeName: z.string(),
  vehicleTypeSlug: slugSchema,
  name: z.string(),
  slug: slugSchema,
  bodyType: z.string().nullable(),
  imageUrl: urlSchema.nullable(),
  isActive: z.boolean(),
  variantCount: z.number().int().min(0).optional(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema
}).openapi("VehicleModel");
z.object({
  makeId: uuidSchema,
  name: z.string().trim().min(1).max(120),
  slug: slugSchema,
  bodyType: z.string().trim().max(60).nullable().optional(),
  imageUrl: urlSchema.nullable().optional(),
  isActive: z.boolean().default(true)
}).openapi("VehicleModelCreate");
z.object({
  makeId: uuidSchema,
  name: z.string().trim().min(1).max(120),
  slug: slugSchema,
  bodyType: z.string().trim().max(60).nullable(),
  imageUrl: urlSchema.nullable(),
  isActive: z.boolean()
}).partial().openapi("VehicleModelUpdate");
listQuerySchema.extend({
  makeId: uuidSchema.optional(),
  vehicleTypeId: uuidSchema.optional(),
  vehicleTypeSlug: slugSchema.optional(),
  bodyType: z.string().trim().max(60).optional(),
  isActive: booleanQuerySchema.optional()
}).openapi("VehicleModelQuery");
var modelYearSchema = z.number().int().min(1900).max(2200).openapi("ModelYear");
z.object({
  id: uuidSchema,
  modelId: uuidSchema,
  modelName: z.string(),
  makeName: z.string(),
  vehicleTypeSlug: slugSchema,
  name: z.string(),
  slug: slugSchema,
  yearFrom: modelYearSchema,
  yearTo: modelYearSchema.nullable(),
  attributes: vehicleAttributesSchema,
  isActive: z.boolean(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema
}).openapi("VehicleVariant");
z.object({
  modelId: uuidSchema,
  name: z.string().trim().min(1).max(120),
  slug: slugSchema,
  yearFrom: modelYearSchema,
  yearTo: modelYearSchema.nullable().optional(),
  attributes: vehicleAttributesSchema.default({}),
  isActive: z.boolean().default(true)
}).refine((v) => v.yearTo == null || v.yearTo >= v.yearFrom, {
  path: ["yearTo"],
  message: "yearTo must not be earlier than yearFrom"
}).openapi("VehicleVariantCreate");
z.object({
  modelId: uuidSchema,
  name: z.string().trim().min(1).max(120),
  slug: slugSchema,
  yearFrom: modelYearSchema,
  yearTo: modelYearSchema.nullable(),
  attributes: vehicleAttributesSchema,
  isActive: z.boolean()
}).partial().openapi("VehicleVariantUpdate");
listQuerySchema.extend({
  modelId: uuidSchema.optional(),
  makeId: uuidSchema.optional(),
  vehicleTypeSlug: slugSchema.optional(),
  year: z.coerce.number().int().min(1900).max(2200).optional(),
  isActive: booleanQuerySchema.optional()
}).openapi("VehicleVariantQuery");
var vehicleSelectionSchema = z.object({
  vehicleTypeSlug: slugSchema.optional(),
  makeId: uuidSchema.optional(),
  modelId: uuidSchema.optional(),
  variantId: uuidSchema.optional(),
  year: z.coerce.number().int().min(1900).max(2200).optional()
}).openapi("VehicleSelection");
z.object({
  id: uuidSchema,
  productId: uuidSchema,
  vehicleTypeId: uuidSchema,
  makeId: uuidSchema.nullable(),
  makeName: z.string().nullable(),
  modelId: uuidSchema.nullable(),
  modelName: z.string().nullable(),
  variantId: uuidSchema.nullable(),
  variantName: z.string().nullable(),
  yearFrom: modelYearSchema.nullable(),
  yearTo: modelYearSchema.nullable(),
  note: z.string().nullable(),
  createdAt: timestampSchema
}).openapi("Fitment");
var fitmentCreateSchema = z.object({
  productId: uuidSchema,
  vehicleTypeId: uuidSchema,
  makeId: uuidSchema.nullable().optional(),
  modelId: uuidSchema.nullable().optional(),
  variantId: uuidSchema.nullable().optional(),
  yearFrom: modelYearSchema.nullable().optional(),
  yearTo: modelYearSchema.nullable().optional(),
  note: z.string().trim().max(240).nullable().optional()
}).refine((v) => v.modelId == null || v.makeId != null, {
  path: ["makeId"],
  message: "makeId is required when modelId is set"
}).refine((v) => v.variantId == null || v.modelId != null, {
  path: ["modelId"],
  message: "modelId is required when variantId is set"
}).openapi("FitmentCreate");
z.object({
  vehicleTypeId: uuidSchema,
  makeId: uuidSchema.nullable(),
  modelId: uuidSchema.nullable(),
  variantId: uuidSchema.nullable(),
  yearFrom: modelYearSchema.nullable(),
  yearTo: modelYearSchema.nullable(),
  note: z.string().trim().max(240).nullable()
}).partial().openapi("FitmentUpdate");
listQuerySchema.extend(vehicleSelectionSchema.shape).extend({ productId: uuidSchema.optional() }).openapi("FitmentQuery");
z.object({ productId: uuidSchema, fitments: z.array(fitmentCreateSchema).min(1).max(200) }).openapi("FitmentBulkCreate");
z.object({ productIds: z.array(uuidSchema).min(1).max(100) }).extend(vehicleSelectionSchema.shape).openapi("FitmentCheckRequest");
z.object({
  productId: uuidSchema,
  fits: z.boolean(),
  matchedFitmentId: uuidSchema.nullable(),
  reason: z.string().nullable()
}).openapi("FitmentCheckResult");
z.object({
  id: uuidSchema,
  userId: uuidSchema,
  vehicleTypeId: uuidSchema,
  vehicleTypeSlug: slugSchema,
  makeId: uuidSchema,
  makeName: z.string(),
  modelId: uuidSchema,
  modelName: z.string(),
  variantId: uuidSchema.nullable(),
  variantName: z.string().nullable(),
  year: modelYearSchema.nullable(),
  nickname: z.string().nullable(),
  registrationNumber: z.string().nullable(),
  isPrimary: z.boolean(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema
}).openapi("UserVehicle");
z.object({
  vehicleTypeId: uuidSchema,
  makeId: uuidSchema,
  modelId: uuidSchema,
  variantId: uuidSchema.nullable().optional(),
  year: modelYearSchema.nullable().optional(),
  nickname: z.string().trim().max(60).nullable().optional(),
  registrationNumber: z.string().trim().max(20).nullable().optional(),
  isPrimary: z.boolean().default(false)
}).openapi("UserVehicleCreate");
z.object({
  vehicleTypeId: uuidSchema,
  makeId: uuidSchema,
  modelId: uuidSchema,
  variantId: uuidSchema.nullable(),
  year: modelYearSchema.nullable(),
  nickname: z.string().trim().max(60).nullable(),
  registrationNumber: z.string().trim().max(20).nullable(),
  isPrimary: z.boolean()
}).partial().openapi("UserVehicleUpdate");
listQuerySchema.extend({ userId: uuidSchema.optional(), vehicleTypeSlug: slugSchema.optional() }).openapi("UserVehicleQuery");
z.object({
  id: uuidSchema,
  title: z.string(),
  subtitle: z.string().nullable(),
  vehicleTypeId: uuidSchema.nullable(),
  makeId: uuidSchema,
  makeName: z.string(),
  modelId: uuidSchema,
  modelName: z.string(),
  variantId: uuidSchema.nullable(),
  variantName: z.string().nullable(),
  year: modelYearSchema.nullable(),
  userVehicleId: uuidSchema.nullable(),
  updatedAt: timestampSchema
}).openapi("ShopForVehicle");
z.object({
  vehicleTypeId: uuidSchema.nullable().optional(),
  makeId: uuidSchema,
  modelId: uuidSchema,
  variantId: uuidSchema.nullable().optional(),
  year: modelYearSchema.nullable().optional(),
  userVehicleId: uuidSchema.nullable().optional()
}).openapi("ShopForVehicleSet");

// src/catalog/index.ts
var ProductCondition = {
  NEW: "new",
  USED: "used"
};
var productConditionSchema = z.enum(ProductCondition).openapi("ProductCondition");
var ProductGrade = { A: "A", B: "B", C: "C" };
var productGradeSchema = z.enum(ProductGrade).openapi("ProductGrade");
var ProductStatus = {
  DRAFT: "draft",
  ACTIVE: "active",
  ARCHIVED: "archived"
};
var productStatusSchema = z.enum(ProductStatus).openapi("ProductStatus");
var CrossRefType = {
  OEM: "oem",
  AFTERMARKET: "aftermarket",
  ALTERNATE: "alternate"
};
var crossRefTypeSchema = z.enum(CrossRefType).openapi("CrossRefType");
var categorySchema = z.object({
  id: uuidSchema,
  parentId: uuidSchema.nullable(),
  name: z.string(),
  slug: slugSchema,
  description: z.string().nullable(),
  imageUrl: urlSchema.nullable(),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
  showOnShopBy: z.boolean(),
  showOnHomeCategories: z.boolean(),
  productCount: z.number().int().min(0).optional(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema
}).openapi("Category");
var categoryTreeNodeSchema = categorySchema.extend({
  get children() {
    return z.array(categoryTreeNodeSchema);
  }
}).openapi("CategoryTreeNode");
z.object({
  parentId: uuidSchema.nullable().optional(),
  name: z.string().trim().min(1).max(120),
  slug: slugSchema,
  description: z.string().trim().max(2e3).nullable().optional(),
  imageUrl: urlSchema.nullable().optional(),
  sortOrder: z.number().int().min(0).default(0),
  isActive: z.boolean().default(true),
  showOnShopBy: z.boolean().default(false),
  showOnHomeCategories: z.boolean().default(false)
}).openapi("CategoryCreate");
z.object({
  parentId: uuidSchema.nullable(),
  name: z.string().trim().min(1).max(120),
  slug: slugSchema,
  description: z.string().trim().max(2e3).nullable(),
  imageUrl: urlSchema.nullable(),
  sortOrder: z.number().int().min(0),
  isActive: z.boolean(),
  showOnShopBy: z.boolean(),
  showOnHomeCategories: z.boolean()
}).partial().openapi("CategoryUpdate");
listQuerySchema.extend({
  parentId: uuidSchema.optional(),
  rootOnly: booleanQuerySchema.optional(),
  isActive: booleanQuerySchema.optional(),
  showOnShopBy: booleanQuerySchema.optional(),
  showOnHomeCategories: booleanQuerySchema.optional()
}).openapi("CategoryQuery");
z.object({
  items: z.array(z.object({ id: uuidSchema, sortOrder: z.number().int().min(0) })).min(1)
}).openapi("CategoryReorder");
z.object({
  id: uuidSchema,
  parentId: uuidSchema.nullable(),
  name: z.string(),
  slug: slugSchema,
  logoUrl: urlSchema.nullable(),
  description: z.string().nullable(),
  isActive: z.boolean(),
  productCount: z.number().int().min(0).optional(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema
}).openapi("Brand");
z.object({
  parentId: uuidSchema.nullable().optional(),
  name: z.string().trim().min(1).max(120),
  slug: slugSchema,
  logoUrl: urlSchema.nullable().optional(),
  description: z.string().trim().max(2e3).nullable().optional(),
  isActive: z.boolean().default(true)
}).openapi("BrandCreate");
z.object({
  parentId: uuidSchema.nullable(),
  name: z.string().trim().min(1).max(120),
  slug: slugSchema,
  logoUrl: urlSchema.nullable(),
  description: z.string().trim().max(2e3).nullable(),
  isActive: z.boolean()
}).partial().openapi("BrandUpdate");
listQuerySchema.extend({ parentId: uuidSchema.optional(), isActive: booleanQuerySchema.optional() }).openapi("BrandQuery");
var productMediaSchema = z.object({
  id: uuidSchema,
  productId: uuidSchema,
  key: mediaKeySchema,
  url: urlSchema,
  kind: mediaKindSchema,
  altText: z.string().nullable(),
  sortOrder: z.number().int(),
  createdAt: timestampSchema
}).openapi("ProductMedia");
var productMediaCreateSchema = z.object({
  key: mediaKeySchema,
  kind: mediaKindSchema.default("image"),
  altText: z.string().trim().max(200).nullable().optional(),
  sortOrder: z.number().int().min(0).default(0)
}).openapi("ProductMediaCreate");
z.object({ media: z.array(productMediaCreateSchema).min(1).max(5) }).openapi("ProductMediaAdd");
z.object({ id: uuidSchema, mediaId: uuidSchema }).openapi("ProductMediaParam");
z.object({
  altText: z.string().trim().max(200).nullable(),
  sortOrder: z.number().int().min(0)
}).partial().openapi("ProductMediaUpdate");
var productCrossRefSchema = z.object({
  id: uuidSchema,
  productId: uuidSchema,
  type: crossRefTypeSchema,
  number: z.string(),
  brandName: z.string().nullable(),
  createdAt: timestampSchema
}).openapi("ProductCrossRef");
var productCrossRefCreateSchema = z.object({
  type: crossRefTypeSchema,
  number: z.string().trim().min(1).max(80),
  brandName: z.string().trim().max(120).nullable().optional()
}).openapi("ProductCrossRefCreate");
z.object({
  type: crossRefTypeSchema,
  number: z.string().trim().min(1).max(80),
  brandName: z.string().trim().max(120).nullable()
}).partial().openapi("ProductCrossRefUpdate");
var dimensionsSchema = z.object({
  lengthMm: z.number().int().positive(),
  widthMm: z.number().int().positive(),
  heightMm: z.number().int().positive()
}).openapi("Dimensions");
var productSummarySchema = z.object({
  id: uuidSchema,
  title: z.string(),
  slug: slugSchema,
  sku: z.string(),
  imageUrl: urlSchema.nullable(),
  price: moneySchema,
  mrp: moneySchema.nullable(),
  specialOfferPrice: moneySchema.nullable(),
  condition: productConditionSchema,
  status: productStatusSchema
}).openapi("ProductSummary");
var productListItemSchema = productSummarySchema.extend({
  brandId: uuidSchema.nullable(),
  brandName: z.string().nullable(),
  categoryId: uuidSchema,
  categoryName: z.string(),
  vehicleMake: z.string().nullable(),
  vehicleModel: z.string().nullable(),
  vehicleVariant: z.string().nullable(),
  vehicleYear: modelYearSchema.nullable(),
  oemNumber: z.string().nullable(),
  grade: productGradeSchema.nullable(),
  quantity: z.number().int().min(0),
  isInStock: z.boolean(),
  isFeatured: z.boolean(),
  reviewAverage: z.number().min(1).max(5).nullable(),
  reviewCount: z.number().int().min(0),
  updatedAt: timestampSchema
}).openapi("ProductListItem");
productListItemSchema.extend({
  description: z.string().nullable(),
  warrantyDays: z.number().int().min(0).nullable(),
  weightGrams: z.number().int().min(0),
  dimensions: dimensionsSchema.nullable(),
  hsnCode: z.string().nullable(),
  taxRate: percentSchema,
  media: z.array(productMediaSchema),
  crossRefs: z.array(productCrossRefSchema),
  fitmentCount: z.number().int().min(0),
  createdAt: timestampSchema
}).openapi("Product");
z.object({
  title: z.string().trim().min(3).max(200),
  slug: slugSchema,
  sku: z.string().trim().min(1).max(64),
  description: z.string().trim().max(2e4).nullable().optional(),
  categoryId: uuidSchema,
  brandId: uuidSchema.nullable().optional(),
  oemNumber: z.string().trim().max(80).nullable().optional(),
  condition: productConditionSchema,
  grade: productGradeSchema.nullable().optional(),
  price: moneySchema,
  mrp: moneySchema.nullable().optional(),
  specialOfferPrice: moneySchema.nullable().optional(),
  vehicleMake: z.string().trim().max(80).nullable().optional(),
  vehicleModel: z.string().trim().max(80).nullable().optional(),
  vehicleVariant: z.string().trim().max(80).nullable().optional(),
  vehicleYear: modelYearSchema.nullable().optional(),
  quantity: z.number().int().min(0).default(0),
  status: productStatusSchema.default("draft"),
  warrantyDays: z.number().int().min(0).nullable().optional(),
  weightGrams: z.number().int().min(0),
  dimensions: dimensionsSchema.nullable().optional(),
  hsnCode: z.string().trim().regex(/^[0-9]{4,8}$/).nullable().optional(),
  taxRate: percentSchema.default(0),
  isFeatured: z.boolean().default(false),
  media: z.array(productMediaCreateSchema).max(5).default([]),
  crossRefs: z.array(productCrossRefCreateSchema).max(50).default([])
}).refine((v) => v.condition === "new" || v.grade != null, {
  path: ["grade"],
  message: "grade is required for used products"
}).refine((v) => v.condition === "new" || v.media.length >= 3, {
  path: ["media"],
  message: "used products need at least three photos"
}).openapi("ProductCreate");
z.object({
  title: z.string().trim().min(3).max(200),
  slug: slugSchema,
  sku: z.string().trim().min(1).max(64),
  description: z.string().trim().max(2e4).nullable(),
  categoryId: uuidSchema,
  brandId: uuidSchema.nullable(),
  oemNumber: z.string().trim().max(80).nullable(),
  condition: productConditionSchema,
  grade: productGradeSchema.nullable(),
  price: moneySchema,
  mrp: moneySchema.nullable(),
  specialOfferPrice: moneySchema.nullable(),
  vehicleMake: z.string().trim().max(80).nullable(),
  vehicleModel: z.string().trim().max(80).nullable(),
  vehicleVariant: z.string().trim().max(80).nullable(),
  vehicleYear: modelYearSchema.nullable(),
  quantity: z.number().int().min(0),
  status: productStatusSchema,
  warrantyDays: z.number().int().min(0).nullable(),
  weightGrams: z.number().int().min(0),
  dimensions: dimensionsSchema.nullable(),
  hsnCode: z.string().trim().regex(/^[0-9]{4,8}$/).nullable(),
  taxRate: percentSchema,
  isFeatured: z.boolean()
}).partial().openapi("ProductUpdate");
var productSortSchema = z.enum(["createdAt", "updatedAt", "price", "title", "quantity"]).openapi("ProductSort");
var productQuerySchema = listQuerySchema.extend(vehicleSelectionSchema.shape).extend({
  sort: productSortSchema.optional(),
  categoryId: uuidSchema.optional(),
  categorySlug: slugSchema.optional(),
  brandId: uuidSchema.optional(),
  condition: productConditionSchema.optional(),
  grade: productGradeSchema.optional(),
  status: productStatusSchema.optional(),
  minPrice: moneySchema.optional(),
  maxPrice: moneySchema.optional(),
  inStock: booleanQuerySchema.optional(),
  isFeatured: booleanQuerySchema.optional(),
  oemNumber: z.string().trim().max(80).optional(),
  userVehicleId: uuidSchema.optional(),
  fitmentYear: z.coerce.number().int().min(1900).max(2200).optional()
}).openapi("ProductQuery");
var facetBucketSchema = z.object({ value: z.string(), label: z.string(), count: z.number().int().min(0) }).openapi("FacetBucket");
var catalogFacetsSchema = z.object({
  categories: z.array(facetBucketSchema),
  brands: z.array(facetBucketSchema),
  conditions: z.array(facetBucketSchema),
  priceRange: z.object({ min: moneySchema, max: moneySchema }).nullable()
}).openapi("CatalogFacets");
productQuerySchema.extend({ withFacets: booleanQuerySchema.optional() }).openapi("CatalogSearchQuery");
z.object({
  items: z.array(productListItemSchema),
  facets: catalogFacetsSchema.nullable(),
  appliedVehicle: vehicleSelectionSchema.nullable()
}).openapi("CatalogSearchResult");
z.object({ id: uuidSchema, title: z.string(), slug: slugSchema, kind: z.string() }).openapi("ProductSuggestion");
z.object({ year: modelYearSchema.optional() }).extend(vehicleSelectionSchema.shape).openapi("ProductCompatibilityQuery");
z.object({ imageUrl: urlSchema, limit: z.coerce.number().int().min(1).max(24).default(12) }).openapi("ProductVisualSearchQuery");
var ProductReviewStatus = {
  PENDING: "pending",
  APPROVED: "approved",
  REJECTED: "rejected"
};
var productReviewStatusSchema = z.enum(ProductReviewStatus).openapi("ProductReviewStatus");
z.object({
  id: uuidSchema,
  productId: uuidSchema,
  productTitle: z.string(),
  userId: uuidSchema,
  userName: z.string(),
  rating: z.number().int().min(1).max(5),
  body: z.string().nullable(),
  status: productReviewStatusSchema,
  createdAt: timestampSchema,
  updatedAt: timestampSchema
}).openapi("ProductReview");
listQuerySchema.extend({
  productId: uuidSchema.optional(),
  status: productReviewStatusSchema.optional(),
  minRating: z.coerce.number().int().min(1).max(5).optional()
}).openapi("ProductReviewQuery");
z.object({ status: productReviewStatusSchema }).openapi("ProductReviewUpdate");
z.object({
  productId: uuidSchema,
  rating: z.number().int().min(1).max(5),
  body: z.string().trim().max(2e3).nullable().optional()
}).openapi("ProductReviewCreate");
z.object({ productId: uuidSchema }).openapi("ProductReviewEligibilityQuery");
var productReviewEligibilityReasonSchema = z.enum(["not_purchased", "already_reviewed"]).openapi("ProductReviewEligibilityReason");
z.object({
  canReview: z.boolean(),
  reason: productReviewEligibilityReasonSchema.optional()
}).openapi("ProductReviewEligibility");

// src/payments/index.ts
var PaymentProvider = { RAZORPAY: "razorpay", COD: "cod" };
var paymentProviderSchema = z.enum(PaymentProvider).openapi("PaymentProvider");
var PaymentMethod = {
  CARD: "card",
  UPI: "upi",
  NETBANKING: "netbanking",
  WALLET: "wallet",
  COD: "cod"
};
var paymentMethodSchema = z.enum(PaymentMethod).openapi("PaymentMethod");
var PaymentStatus = {
  CREATED: "created",
  PENDING: "pending",
  AUTHORIZED: "authorized",
  CAPTURED: "captured",
  FAILED: "failed",
  REFUNDED: "refunded",
  PARTIALLY_REFUNDED: "partially_refunded"
};
var paymentStatusSchema = z.enum(PaymentStatus).openapi("PaymentStatus");
var RefundStatus = {
  REQUESTED: "requested",
  PROCESSING: "processing",
  COMPLETED: "completed",
  FAILED: "failed"
};
var refundStatusSchema = z.enum(RefundStatus).openapi("RefundStatus");
var RefundReason = {
  CANCELLED: "cancelled",
  RETURNED: "returned",
  DAMAGED: "damaged",
  WRONG_ITEM: "wrong_item",
  GOODWILL: "goodwill"
};
var refundReasonSchema = z.enum(RefundReason).openapi("RefundReason");
z.object({
  orderId: uuidSchema,
  provider: paymentProviderSchema,
  idempotencyKey: idempotencyKeySchema
}).openapi("PaymentIntentRequest");
var paymentIntentSchema = z.object({
  id: uuidSchema,
  orderId: uuidSchema,
  provider: paymentProviderSchema,
  providerOrderId: z.string().nullable(),
  providerKeyId: z.string().nullable(),
  amount: moneySchema,
  currency: currencyCodeSchema,
  status: paymentStatusSchema,
  expiresAt: timestampSchema.nullable(),
  createdAt: timestampSchema
}).openapi("PaymentIntent");
z.object({
  orderId: uuidSchema,
  provider: paymentProviderSchema,
  providerOrderId: z.string().trim().min(1).max(128),
  providerPaymentId: z.string().trim().min(1).max(128),
  providerSignature: z.string().trim().min(1).max(512),
  idempotencyKey: idempotencyKeySchema
}).openapi("PaymentVerifyRequest");
var paymentSchema = z.object({
  id: uuidSchema,
  orderId: uuidSchema,
  orderNumber: z.string(),
  userId: uuidSchema,
  provider: paymentProviderSchema,
  method: paymentMethodSchema.nullable(),
  amount: moneySchema,
  refundedAmount: moneySchema,
  currency: currencyCodeSchema,
  status: paymentStatusSchema,
  providerOrderId: z.string().nullable(),
  providerPaymentId: z.string().nullable(),
  failureReason: z.string().nullable(),
  capturedAt: timestampSchema.nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema
}).openapi("Payment");
z.object({ payment: paymentSchema, orderStatus: z.string() }).openapi("PaymentVerifyResponse");
z.object({
  orderId: uuidSchema,
  provider: paymentProviderSchema,
  method: paymentMethodSchema.optional(),
  amount: moneySchema,
  idempotencyKey: idempotencyKeySchema
}).openapi("PaymentCreate");
z.object({ status: paymentStatusSchema, failureReason: z.string().trim().max(500).nullable() }).partial().openapi("PaymentUpdate");
listQuerySchema.extend({
  orderId: uuidSchema.optional(),
  userId: uuidSchema.optional(),
  provider: paymentProviderSchema.optional(),
  method: paymentMethodSchema.optional(),
  status: paymentStatusSchema.optional(),
  dateFrom: timestampSchema.optional(),
  dateTo: timestampSchema.optional()
}).openapi("PaymentQuery");
var refundSchema = z.object({
  id: uuidSchema,
  paymentId: uuidSchema,
  orderId: uuidSchema,
  orderNumber: z.string(),
  amount: moneySchema,
  currency: currencyCodeSchema,
  reason: refundReasonSchema,
  note: z.string().nullable(),
  status: refundStatusSchema,
  providerRefundId: z.string().nullable(),
  failureReason: z.string().nullable(),
  processedAt: timestampSchema.nullable(),
  createdAt: timestampSchema
}).openapi("Refund");
z.object({
  paymentId: uuidSchema,
  amount: moneySchema,
  reason: refundReasonSchema,
  note: z.string().trim().max(500).nullable().optional(),
  idempotencyKey: idempotencyKeySchema
}).openapi("RefundCreate");
z.object({
  status: refundStatusSchema,
  note: z.string().trim().max(500).nullable(),
  failureReason: z.string().trim().max(500).nullable()
}).partial().openapi("RefundUpdate");
listQuerySchema.extend({
  orderId: uuidSchema.optional(),
  paymentId: uuidSchema.optional(),
  status: refundStatusSchema.optional(),
  reason: refundReasonSchema.optional(),
  dateFrom: timestampSchema.optional(),
  dateTo: timestampSchema.optional()
}).openapi("RefundQuery");
z.object({
  provider: paymentProviderSchema,
  event: z.string().trim().min(1).max(120),
  providerEventId: z.string().trim().min(1).max(128),
  payload: z.record(z.string(), z.unknown())
}).openapi("PaymentWebhookEvent");

// src/shipping/index.ts
var ShipmentStatus = {
  PENDING: "pending",
  LABEL_CREATED: "label_created",
  PICKED_UP: "picked_up",
  IN_TRANSIT: "in_transit",
  OUT_FOR_DELIVERY: "out_for_delivery",
  DELIVERED: "delivered",
  FAILED: "failed",
  RETURNED: "returned"
};
var shipmentStatusSchema = z.enum(ShipmentStatus).openapi("ShipmentStatus");
var carrierSchema = z.string().trim().min(1).max(80).openapi("Carrier");
var trackingNumberSchema = z.string().trim().min(3).max(64).regex(/^[A-Za-z0-9-]+$/).openapi("TrackingNumber");
var shipmentSchema = z.object({
  id: uuidSchema,
  orderId: uuidSchema,
  orderNumber: z.string(),
  carrier: carrierSchema,
  trackingNumber: trackingNumberSchema.nullable(),
  trackingUrl: urlSchema.nullable(),
  status: shipmentStatusSchema,
  weightGrams: z.number().int().min(0),
  shippingCharge: moneySchema,
  dispatchedAt: timestampSchema.nullable(),
  deliveredAt: timestampSchema.nullable(),
  estimatedDeliveryAt: timestampSchema.nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema
}).openapi("Shipment");
z.object({
  orderId: uuidSchema,
  carrier: carrierSchema,
  trackingNumber: trackingNumberSchema.nullable().optional(),
  trackingUrl: urlSchema.nullable().optional(),
  weightGrams: z.number().int().min(0),
  shippingCharge: moneySchema,
  estimatedDeliveryAt: timestampSchema.nullable().optional()
}).openapi("ShipmentCreate");
z.object({
  carrier: carrierSchema,
  trackingNumber: trackingNumberSchema.nullable(),
  trackingUrl: urlSchema.nullable(),
  status: shipmentStatusSchema,
  weightGrams: z.number().int().min(0),
  shippingCharge: moneySchema,
  dispatchedAt: timestampSchema.nullable(),
  deliveredAt: timestampSchema.nullable(),
  estimatedDeliveryAt: timestampSchema.nullable()
}).partial().openapi("ShipmentUpdate");
listQuerySchema.extend({
  orderId: uuidSchema.optional(),
  status: shipmentStatusSchema.optional(),
  carrier: carrierSchema.optional(),
  trackingNumber: trackingNumberSchema.optional(),
  dateFrom: timestampSchema.optional(),
  dateTo: timestampSchema.optional()
}).openapi("ShipmentQuery");
var trackingEventSchema = z.object({
  id: uuidSchema,
  shipmentId: uuidSchema,
  status: shipmentStatusSchema,
  description: z.string(),
  location: z.string().nullable(),
  occurredAt: timestampSchema
}).openapi("TrackingEvent");
z.object({
  status: shipmentStatusSchema,
  description: z.string().trim().min(1).max(240),
  location: z.string().trim().max(120).nullable().optional(),
  occurredAt: timestampSchema
}).openapi("TrackingEventCreate");
z.object({ shipment: shipmentSchema, events: z.array(trackingEventSchema) }).openapi("ShipmentTracking");
var weightBandSchema = z.object({
  id: uuidSchema,
  label: z.string(),
  minGrams: z.number().int().min(0),
  maxGrams: z.number().int().positive().nullable(),
  charge: moneySchema,
  etaDays: z.number().int().positive().nullable(),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema
}).openapi("WeightBand");
z.object({
  label: z.string().trim().min(1).max(80),
  minGrams: z.number().int().min(0),
  maxGrams: z.number().int().positive().nullable().optional(),
  charge: moneySchema,
  etaDays: z.number().int().positive().nullable().optional(),
  sortOrder: z.number().int().min(0).default(0),
  isActive: z.boolean().default(true)
}).refine((v) => v.maxGrams == null || v.maxGrams > v.minGrams, {
  path: ["maxGrams"],
  message: "maxGrams must be greater than minGrams"
}).openapi("WeightBandCreate");
z.object({
  label: z.string().trim().min(1).max(80),
  minGrams: z.number().int().min(0),
  maxGrams: z.number().int().positive().nullable(),
  charge: moneySchema,
  etaDays: z.number().int().positive().nullable(),
  sortOrder: z.number().int().min(0),
  isActive: z.boolean()
}).partial().openapi("WeightBandUpdate");
listQuerySchema.extend({ isActive: booleanQuerySchema.optional() }).openapi("WeightBandQuery");
z.object({
  pincode: pincodeSchema,
  weightGrams: z.number().int().min(0),
  orderValue: moneySchema
}).openapi("ShippingQuoteRequest");
var shippingQuoteSchema = z.object({
  serviceable: z.boolean(),
  charge: moneySchema,
  freeShippingApplied: z.boolean(),
  band: weightBandSchema.nullable(),
  etaDays: z.number().int().positive().nullable(),
  message: z.string().nullable()
}).openapi("ShippingQuote");
z.object({ pincode: pincodeSchema }).openapi("PincodeCheckRequest");
z.object({ pincode: pincodeSchema, serviceable: z.boolean(), etaDays: z.number().int().nullable() }).openapi("PincodeCheck");

// src/promotions/index.ts
var CouponType = {
  PERCENTAGE: "percentage",
  FIXED: "fixed",
  FREE_SHIPPING: "free_shipping"
};
var couponTypeSchema = z.enum(CouponType).openapi("CouponType");
var CouponStatus = {
  SCHEDULED: "scheduled",
  ACTIVE: "active",
  EXPIRED: "expired",
  EXHAUSTED: "exhausted",
  DISABLED: "disabled"
};
var couponStatusSchema = z.enum(CouponStatus).openapi("CouponStatus");
var CouponRejectionReason = {
  NOT_FOUND: "not_found",
  EXPIRED: "expired",
  NOT_STARTED: "not_started",
  DISABLED: "disabled",
  USAGE_LIMIT_REACHED: "usage_limit_reached",
  USER_LIMIT_REACHED: "user_limit_reached",
  MIN_ORDER_NOT_MET: "min_order_not_met",
  NOT_APPLICABLE: "not_applicable"
};
var couponRejectionReasonSchema = z.enum(CouponRejectionReason).openapi("CouponRejectionReason");
var couponCodeSchema = z.string().trim().toUpperCase().min(3).max(32).regex(/^[A-Z0-9_-]+$/).openapi("CouponCode");
var couponScopeSchema = z.object({
  categoryIds: z.array(uuidSchema).max(100),
  brandIds: z.array(uuidSchema).max(100),
  productIds: z.array(uuidSchema).max(500)
}).openapi("CouponScope");
z.object({
  id: uuidSchema,
  code: couponCodeSchema,
  description: z.string().nullable(),
  type: couponTypeSchema,
  value: moneySchema,
  percentValue: percentSchema.nullable(),
  minOrderValue: moneySchema.nullable(),
  maxDiscount: moneySchema.nullable(),
  usageLimit: z.number().int().positive().nullable(),
  perUserLimit: z.number().int().positive().nullable(),
  usedCount: z.number().int().min(0),
  startsAt: timestampSchema,
  endsAt: timestampSchema.nullable(),
  isActive: z.boolean(),
  status: couponStatusSchema,
  scope: couponScopeSchema,
  createdAt: timestampSchema,
  updatedAt: timestampSchema
}).openapi("Coupon");
z.object({
  code: couponCodeSchema,
  description: z.string().trim().max(500).nullable().optional(),
  type: couponTypeSchema,
  value: moneySchema.optional(),
  percentValue: percentSchema.optional(),
  minOrderValue: moneySchema.nullable().optional(),
  maxDiscount: moneySchema.nullable().optional(),
  usageLimit: z.number().int().positive().nullable().optional(),
  perUserLimit: z.number().int().positive().nullable().optional(),
  startsAt: timestampSchema,
  endsAt: timestampSchema.nullable().optional(),
  isActive: z.boolean().default(true),
  scope: couponScopeSchema.default({ categoryIds: [], brandIds: [], productIds: [] })
}).refine((v) => v.type !== "percentage" || v.percentValue != null, {
  path: ["percentValue"],
  message: "percentValue is required for a percentage coupon"
}).refine((v) => v.type !== "fixed" || v.value != null, {
  path: ["value"],
  message: "value is required for a fixed coupon"
}).openapi("CouponCreate");
z.object({
  description: z.string().trim().max(500).nullable(),
  value: moneySchema,
  percentValue: percentSchema,
  minOrderValue: moneySchema.nullable(),
  maxDiscount: moneySchema.nullable(),
  usageLimit: z.number().int().positive().nullable(),
  perUserLimit: z.number().int().positive().nullable(),
  startsAt: timestampSchema,
  endsAt: timestampSchema.nullable(),
  isActive: z.boolean(),
  scope: couponScopeSchema
}).partial().openapi("CouponUpdate");
listQuerySchema.extend({
  type: couponTypeSchema.optional(),
  status: couponStatusSchema.optional(),
  isActive: booleanQuerySchema.optional(),
  activeOn: timestampSchema.optional()
}).openapi("CouponQuery");
z.object({
  id: uuidSchema,
  couponId: uuidSchema,
  code: couponCodeSchema,
  userId: uuidSchema,
  userName: z.string(),
  orderId: uuidSchema,
  orderNumber: z.string(),
  discountAmount: moneySchema,
  redeemedAt: timestampSchema
}).openapi("CouponRedemption");
listQuerySchema.extend({
  couponId: uuidSchema.optional(),
  userId: uuidSchema.optional(),
  dateFrom: timestampSchema.optional(),
  dateTo: timestampSchema.optional()
}).openapi("CouponRedemptionQuery");
z.object({ code: couponCodeSchema, cartId: uuidSchema.optional() }).openapi("CouponValidateRequest");
z.object({
  valid: z.boolean(),
  code: couponCodeSchema,
  type: couponTypeSchema.nullable(),
  discount: moneySchema,
  freeShipping: z.boolean(),
  reason: couponRejectionReasonSchema.nullable(),
  message: z.string().nullable()
}).openapi("CouponValidation");
var couponAppliedSchema = z.object({
  id: uuidSchema,
  code: couponCodeSchema,
  type: couponTypeSchema,
  discount: moneySchema,
  freeShipping: z.boolean()
}).openapi("CouponApplied");
var HomepageBannerStatus = { ACTIVE: "active", INACTIVE: "inactive" };
z.enum(HomepageBannerStatus).openapi("HomepageBannerStatus");
z.object({
  id: uuidSchema,
  title: z.string(),
  imageUrl: urlSchema,
  linkUrl: urlSchema.nullable(),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
  startsAt: timestampSchema.nullable(),
  endsAt: timestampSchema.nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema
}).openapi("HomepageBanner");
var homepageBannerCreateSchema = z.object({
  title: z.string().trim().min(1).max(120),
  imageUrl: urlSchema,
  linkUrl: urlSchema.nullable().optional(),
  sortOrder: z.number().int().min(0).default(0),
  isActive: z.boolean().default(true),
  startsAt: timestampSchema.nullable().optional(),
  endsAt: timestampSchema.nullable().optional()
}).openapi("HomepageBannerCreate");
homepageBannerCreateSchema.partial().openapi("HomepageBannerUpdate");
listQuerySchema.extend({ isActive: booleanQuerySchema.optional() }).openapi("HomepageBannerQuery");

// src/cart/index.ts
var CartItemIssue = {
  OUT_OF_STOCK: "out_of_stock",
  QUANTITY_REDUCED: "quantity_reduced",
  PRICE_CHANGED: "price_changed",
  UNAVAILABLE: "unavailable",
  FITMENT_MISMATCH: "fitment_mismatch"
};
var cartItemIssueSchema = z.enum(CartItemIssue).openapi("CartItemIssue");
var cartItemSchema = z.object({
  id: uuidSchema,
  productId: uuidSchema,
  product: productSummarySchema,
  quantity: z.number().int().positive(),
  unitPrice: moneySchema,
  lineTotal: moneySchema,
  maxQuantity: z.number().int().min(0),
  issues: z.array(cartItemIssueSchema),
  addedAt: timestampSchema
}).openapi("CartItem");
var cartFitmentWarningSchema = z.object({
  productId: uuidSchema,
  userVehicleId: uuidSchema.nullable(),
  message: z.string()
}).openapi("CartFitmentWarning");
z.object({
  id: uuidSchema,
  userId: uuidSchema,
  items: z.array(cartItemSchema),
  itemCount: z.number().int().min(0),
  subtotal: moneySchema,
  currency: currencyCodeSchema,
  fitmentWarnings: z.array(cartFitmentWarningSchema),
  hasIssues: z.boolean(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema
}).openapi("Cart");
var cartItemCreateSchema = z.object({
  productId: uuidSchema,
  quantity: z.number().int().positive().max(99).default(1),
  userVehicleId: uuidSchema.nullable().optional()
}).openapi("CartItemCreate");
z.object({ quantity: z.number().int().positive().max(99) }).partial().openapi("CartItemUpdate");
z.object({ itemId: uuidSchema }).openapi("CartItemParam");
z.object({ items: z.array(cartItemCreateSchema).min(1).max(100) }).openapi("CartMergeRequest");
listQuerySchema.extend({
  userId: uuidSchema.optional(),
  updatedBefore: timestampSchema.optional(),
  minSubtotal: moneySchema.optional()
}).openapi("CartQuery");
z.object({
  id: uuidSchema,
  userId: uuidSchema,
  userName: z.string(),
  userEmail: z.string(),
  itemCount: z.number().int().min(0),
  subtotal: moneySchema,
  currency: currencyCodeSchema,
  updatedAt: timestampSchema
}).openapi("CartSummary");
z.object({ itemCount: z.number().int().min(0), subtotal: moneySchema }).openapi("CartCount");

// src/checkout/index.ts
var AddressType = { SHIPPING: "shipping", BILLING: "billing", BOTH: "both" };
var addressTypeSchema = z.enum(AddressType).openapi("AddressType");
var addressSchema = z.object({
  id: uuidSchema,
  userId: uuidSchema,
  label: z.string().nullable(),
  fullName: z.string(),
  phone: phoneSchema,
  line1: z.string(),
  line2: z.string().nullable(),
  landmark: z.string().nullable(),
  city: z.string(),
  state: z.string(),
  pincode: pincodeSchema,
  country: countryCodeSchema,
  type: addressTypeSchema,
  isDefaultShipping: z.boolean(),
  isDefaultBilling: z.boolean(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema
}).openapi("Address");
z.object({
  label: z.string().trim().max(40).nullable().optional(),
  fullName: z.string().trim().min(1).max(120),
  phone: phoneSchema,
  line1: z.string().trim().min(1).max(200),
  line2: z.string().trim().max(200).nullable().optional(),
  landmark: z.string().trim().max(120).nullable().optional(),
  city: z.string().trim().min(1).max(80),
  state: z.string().trim().min(1).max(80),
  pincode: pincodeSchema,
  country: countryCodeSchema,
  type: addressTypeSchema.default("both"),
  isDefaultShipping: z.boolean().default(false),
  isDefaultBilling: z.boolean().default(false)
}).openapi("AddressCreate");
z.object({
  label: z.string().trim().max(40).nullable(),
  fullName: z.string().trim().min(1).max(120),
  phone: phoneSchema,
  line1: z.string().trim().min(1).max(200),
  line2: z.string().trim().max(200).nullable(),
  landmark: z.string().trim().max(120).nullable(),
  city: z.string().trim().min(1).max(80),
  state: z.string().trim().min(1).max(80),
  pincode: pincodeSchema,
  country: countryCodeSchema,
  type: addressTypeSchema,
  isDefaultShipping: z.boolean(),
  isDefaultBilling: z.boolean()
}).partial().openapi("AddressUpdate");
listQuerySchema.extend({ userId: uuidSchema.optional(), type: addressTypeSchema.optional() }).openapi("AddressQuery");
var taxLineSchema = z.object({
  label: z.string(),
  rate: percentSchema,
  taxableAmount: moneySchema,
  amount: moneySchema
}).openapi("TaxLine");
var CheckoutIssue = {
  CART_EMPTY: "cart_empty",
  ITEM_OUT_OF_STOCK: "item_out_of_stock",
  ITEM_PRICE_CHANGED: "item_price_changed",
  ADDRESS_MISSING: "address_missing",
  PINCODE_NOT_SERVICEABLE: "pincode_not_serviceable",
  COUPON_INVALID: "coupon_invalid",
  MINIMUM_ORDER_NOT_MET: "minimum_order_not_met"
};
var checkoutIssueSchema = z.enum(CheckoutIssue).openapi("CheckoutIssue");
var checkoutIssueDetailSchema = z.object({
  code: checkoutIssueSchema,
  message: z.string(),
  productId: uuidSchema.nullable(),
  blocking: z.boolean()
}).openapi("CheckoutIssueDetail");
z.object({
  shippingAddressId: uuidSchema.optional(),
  billingAddressId: uuidSchema.optional(),
  couponCode: couponCodeSchema.optional(),
  pincode: pincodeSchema.optional(),
  userVehicleId: uuidSchema.nullable().optional()
}).openapi("CheckoutQuoteRequest");
var checkoutTotalsSchema = z.object({
  subtotal: moneySchema,
  discount: moneySchema,
  taxTotal: moneySchema,
  shippingTotal: moneySchema,
  grandTotal: moneySchema,
  currency: currencyCodeSchema
}).openapi("CheckoutTotals");
z.object({
  items: z.array(cartItemSchema),
  totals: checkoutTotalsSchema,
  taxBreakdown: z.array(taxLineSchema),
  shipping: shippingQuoteSchema,
  coupon: couponAppliedSchema.nullable(),
  shippingAddress: addressSchema.nullable(),
  billingAddress: addressSchema.nullable(),
  availableProviders: z.array(paymentProviderSchema),
  issues: z.array(checkoutIssueDetailSchema),
  canPlaceOrder: z.boolean(),
  expiresAt: timestampSchema
}).openapi("CheckoutQuote");
z.object({
  shippingAddressId: uuidSchema,
  billingAddressId: uuidSchema,
  couponCode: couponCodeSchema.nullable().optional(),
  provider: paymentProviderSchema,
  idempotencyKey: idempotencyKeySchema,
  expectedTotal: moneySchema,
  notes: z.string().trim().max(500).nullable().optional(),
  userVehicleId: uuidSchema.nullable().optional()
}).openapi("CheckoutRequest");
z.object({
  orderId: uuidSchema,
  orderNumber: z.string(),
  status: z.string(),
  totals: checkoutTotalsSchema,
  payment: paymentIntentSchema.nullable()
}).openapi("CheckoutResult");
z.object({ shippingAddressId: uuidSchema, billingAddressId: uuidSchema.optional() }).openapi("AddressSelection");

// src/orders/index.ts
var OrderStatus = {
  PENDING: "pending",
  CONFIRMED: "confirmed",
  PACKED: "packed",
  SHIPPED: "shipped",
  OUT_FOR_DELIVERY: "out_for_delivery",
  DELIVERED: "delivered",
  CANCELLED: "cancelled",
  RETURNED: "returned",
  REFUNDED: "refunded"
};
var orderStatusSchema = z.enum(OrderStatus).openapi("OrderStatus");
var CancellationReason = {
  CUSTOMER_REQUEST: "customer_request",
  OUT_OF_STOCK: "out_of_stock",
  PAYMENT_FAILED: "payment_failed",
  UNDELIVERABLE: "undeliverable",
  FRAUD_SUSPECTED: "fraud_suspected"
};
var cancellationReasonSchema = z.enum(CancellationReason).openapi("CancellationReason");
var orderNumberSchema = z.string().trim().min(4).max(32).regex(/^[A-Z0-9-]+$/).openapi("OrderNumber");
var orderAddressSchema = z.object({
  fullName: z.string(),
  phone: phoneSchema,
  line1: z.string(),
  line2: z.string().nullable(),
  landmark: z.string().nullable(),
  city: z.string(),
  state: z.string(),
  pincode: pincodeSchema,
  country: countryCodeSchema
}).openapi("OrderAddress");
var orderItemSchema = z.object({
  id: uuidSchema,
  productId: uuidSchema,
  title: z.string(),
  slug: z.string(),
  sku: z.string(),
  imageUrl: urlSchema.nullable(),
  brandName: z.string().nullable(),
  oemNumber: z.string().nullable(),
  condition: productConditionSchema,
  grade: productGradeSchema.nullable(),
  quantity: z.number().int().positive(),
  unitPrice: moneySchema,
  mrp: moneySchema.nullable(),
  discount: moneySchema,
  taxRate: percentSchema,
  taxAmount: moneySchema,
  lineTotal: moneySchema,
  warrantyDays: z.number().int().min(0).nullable()
}).openapi("OrderItem");
var orderTotalsSchema = z.object({
  subtotal: moneySchema,
  discount: moneySchema,
  taxTotal: moneySchema,
  shippingTotal: moneySchema,
  grandTotal: moneySchema,
  refundedTotal: moneySchema,
  currency: currencyCodeSchema
}).openapi("OrderTotals");
var orderStatusHistoryEntrySchema = z.object({
  id: uuidSchema,
  fromStatus: orderStatusSchema.nullable(),
  toStatus: orderStatusSchema,
  note: z.string().nullable(),
  actor: actorSchema.nullable(),
  createdAt: timestampSchema
}).openapi("OrderStatusHistoryEntry");
var orderCustomerSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  email: z.string(),
  phone: phoneSchema.nullable()
}).openapi("OrderCustomer");
var orderListItemSchema = z.object({
  id: uuidSchema,
  orderNumber: orderNumberSchema,
  status: orderStatusSchema,
  paymentStatus: paymentStatusSchema,
  customer: orderCustomerSchema,
  itemCount: z.number().int().positive(),
  grandTotal: moneySchema,
  currency: currencyCodeSchema,
  placedAt: timestampSchema,
  updatedAt: timestampSchema
}).openapi("OrderListItem");
var orderSchema = orderListItemSchema.extend({
  items: z.array(orderItemSchema),
  totals: orderTotalsSchema,
  taxBreakdown: z.array(taxLineSchema),
  coupon: couponAppliedSchema.nullable(),
  shippingAddress: orderAddressSchema,
  billingAddress: orderAddressSchema,
  payments: z.array(paymentSchema),
  refunds: z.array(refundSchema),
  shipments: z.array(shipmentSchema),
  statusHistory: z.array(orderStatusHistoryEntrySchema),
  notes: z.string().nullable(),
  internalNotes: z.string().nullable(),
  cancelledAt: timestampSchema.nullable(),
  cancellationReason: cancellationReasonSchema.nullable(),
  deliveredAt: timestampSchema.nullable()
}).openapi("Order");
var orderItemCreateSchema = z.object({ productId: uuidSchema, quantity: z.number().int().positive().max(99) }).openapi("OrderItemCreate");
var orderCreateSchema = z.object({
  userId: uuidSchema,
  items: z.array(orderItemCreateSchema).min(1).max(100),
  shippingAddressId: uuidSchema,
  billingAddressId: uuidSchema,
  couponCode: z.string().trim().max(32).nullable().optional(),
  notes: z.string().trim().max(500).nullable().optional(),
  idempotencyKey: idempotencyKeySchema
}).openapi("OrderCreate");
var orderUpdateSchema = z.object({
  notes: z.string().trim().max(500).nullable(),
  internalNotes: z.string().trim().max(2e3).nullable(),
  shippingAddress: orderAddressSchema
}).partial().openapi("OrderUpdate");
var orderSortSchema = z.enum(["placedAt", "updatedAt", "grandTotal", "orderNumber"]).openapi("OrderSort");
var orderQuerySchema = listQuerySchema.extend({
  sort: orderSortSchema.optional(),
  status: orderStatusSchema.optional(),
  statuses: z.array(orderStatusSchema).optional(),
  paymentStatus: paymentStatusSchema.optional(),
  userId: uuidSchema.optional(),
  orderNumber: orderNumberSchema.optional(),
  minTotal: moneySchema.optional(),
  maxTotal: moneySchema.optional(),
  dateFrom: timestampSchema.optional(),
  dateTo: timestampSchema.optional()
}).openapi("OrderQuery");
var orderStatusTransitionSchema = z.object({ from: orderStatusSchema, to: orderStatusSchema }).openapi("OrderStatusTransition");
var orderTransitionRequestSchema = z.object({
  status: orderStatusSchema,
  note: z.string().trim().max(500).nullable().optional(),
  notifyCustomer: z.boolean().default(true)
}).openapi("OrderTransitionRequest");
var orderCancelRequestSchema = z.object({
  reason: cancellationReasonSchema,
  note: z.string().trim().max(500).nullable().optional(),
  refund: z.boolean().default(true)
}).openapi("OrderCancelRequest");
var orderAllowedTransitionsSchema = z.object({ current: orderStatusSchema, allowed: z.array(orderStatusSchema) }).openapi("OrderAllowedTransitions");
var orderStatusCountSchema = z.object({ status: orderStatusSchema, count: z.number().int().min(0) }).openapi("OrderStatusCount");

export { CancellationReason, OrderStatus, cancellationReasonSchema, orderAddressSchema, orderAllowedTransitionsSchema, orderCancelRequestSchema, orderCreateSchema, orderCustomerSchema, orderItemCreateSchema, orderItemSchema, orderListItemSchema, orderNumberSchema, orderQuerySchema, orderSchema, orderSortSchema, orderStatusCountSchema, orderStatusHistoryEntrySchema, orderStatusSchema, orderStatusTransitionSchema, orderTotalsSchema, orderTransitionRequestSchema, orderUpdateSchema };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map